import React, { memo } from 'react';
import { Formik, Form, getIn } from 'formik';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import FormField from './FormField';
import * as Yup from 'yup';
import moment from 'moment';

interface Option {
  label: string;
  value: string | number;
}

interface ExtraProps {
  prefix?: string;
  inputProps?: object;
}

interface FieldProps {
  name: string;
  label: string;
  type?: 'text' | 'date' | 'select' | 'numeric' | 'tel';
  options?: Option[];
  dependentField?: string;
  dependentOptions?: Option[];
  fetchOptions?: (value: string) => void;
  extra?: ExtraProps;
  disabled?: boolean;
  format?: string;
}

interface ProfileDetails
  extends Record<
    string,
    string | number | object | null | undefined | boolean
  > {
  patient_platform_first_login?: boolean;
}

interface SectionFormProps {
  initialValues: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  };
  validationSchema: Yup.ObjectSchema<object>;
  fields: FieldProps[];
  section: 'personalDetails' | 'contactDetails';
  edit: boolean;
  setEditMode: (section: 'personalDetails' | 'contactDetails') => void;
  updateProfileDetails: (
    values: ProfileDetails,
    setEditMode: () => void
  ) => void;
}

const SectionForm: React.FC<SectionFormProps> = ({
  initialValues,
  validationSchema,
  fields,
  section,
  edit,
  setEditMode,
  updateProfileDetails,
}) => {
  const handleEdit = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    if (!edit) {
      event.preventDefault();
      setEditMode(section);
    }
  };
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        if (edit) {
          updateProfileDetails(values, () => setEditMode(section));
        } else {
          setEditMode(section);
        }
      }}
      enableReinitialize
    >
      {({ values }) => (
        <Form>
          <Box sx={{ gap: 2, mb: 3 }} className="d-flex align-items-center">
            <Typography
              variant="h6"
              sx={{ flex: 1, color: 'grey.900', fontWeight: 600 }}
            >
              {section === 'personalDetails'
                ? 'Personal details'
                : 'Contact details'}
            </Typography>

            <Button
              type={edit ? 'submit' : 'button'}
              variant="text"
              sx={{
                minWidth: 'unset',
                color: 'tertiary.main',
                fontWeight: 600,
                textDecoration: 'none',
                paddingX: 1,
                borderRadius: 1,
              }}
              onClick={handleEdit}
            >
              {edit ? 'Save changes' : 'Edit'}
            </Button>
          </Box>
          <List sx={{ gap: 2, py: 0 }} className="d-flex flex-column">
            {fields.map((field) => (
              <ListItem key={field.name} sx={{ p: 0 }}>
                {edit ? (
                  <FormField
                    name={field.name}
                    label={field.label}
                    type={field.type}
                    options={field.options}
                    dependentField={field.dependentField}
                    dependentOptions={field.dependentOptions}
                    fetchOptions={field.fetchOptions}
                    extra={field.extra}
                    disabled={field.disabled}
                  />
                ) : (
                  <ListItemText
                    primary={field.label}
                    secondary={
                      <Typography
                        sx={{ fontWeight: 600, color: 'text.primary' }}
                      >
                        {/* Handle nested values */}
                        {getIn(values, field.name) ? (
                          <>
                            {field.extra?.prefix}{' '}
                            {field.type === 'select'
                              ? field.dependentField
                                ? // If dependentField is present, find in dependentOptions
                                  field.dependentOptions?.find(
                                    (option) =>
                                      option.value === getIn(values, field.name)
                                  )?.label || '-'
                                : // Otherwise, find in the regular options array
                                  field.options?.find(
                                    (option) =>
                                      option.value === getIn(values, field.name)
                                  )?.label || '-'
                              : // For other types, just show the value
                                field.type === 'date' && field?.format
                                ? moment(getIn(values, field.name)).format(
                                    field?.format
                                  )
                                : getIn(values, field.name)}
                          </>
                        ) : (
                          <>-</>
                        )}
                      </Typography>
                    }
                    sx={{ color: 'text.secondary', my: 0 }}
                  />
                )}
              </ListItem>
            ))}
          </List>
        </Form>
      )}
    </Formik>
  );
};

export default memo(SectionForm);
